import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getProduct, createProduct, updateProduct } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import ImageUploader from "@/components/ImageUploader";
import { ArrowLeft, Plus, Trash2, Save, Eye, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import GradientBackground from "@/components/GradientBackground";
import LandingSectionsEditor from "@/components/admin/LandingSectionsEditor";

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "fallback";

function PasswordGate({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem("admin_auth", "true");
      onSuccess();
    } else {
      setError(true);
      setPassword("");
    }
  };

  return (
    <div className="min-h-screen text-white relative overflow-hidden flex items-center justify-center">
      <GradientBackground />
      <div className="relative z-10 w-full max-w-sm mx-4">
        <div className="bg-black/60 backdrop-blur-xl border-2 border-[#ff0090]/40 rounded-3xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#ff0090]/20 border-2 border-[#ff0090] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#ff0090]" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-white">Admin-Bereich</h1>
            <p className="text-white/60 text-sm mt-2">Bitte Codewort eingeben</p>
          </div>
          <form onSubmit={handleSubmit}>
            <Input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(false); }}
              placeholder="Codewort..."
              className={`w-full bg-black/40 border-2 ${error ? 'border-red-500' : 'border-[#ff0090]/40'} rounded-xl text-white placeholder:text-white/40 h-12 mb-4`}
              autoFocus
            />
            {error && <p className="text-red-400 text-sm text-center mb-4">Falsches Codewort</p>}
            <Button type="submit" className="w-full h-12 bg-[#ff0090] hover:bg-[#ff0090]/80 text-white font-semibold rounded-xl">
              Einloggen
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-white/50 hover:text-white">← Zurück zur Startseite</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem("admin_auth");
    if (auth === "true") setIsAuthenticated(true);
  }, []);

  const [form, setForm] = useState({
    title: '',
    description: '',
    image_url: '',
    cta_buttons: [],
    grid_size: 'standard',
    is_published: true,
    order: 0,
    accent_color: '#6366f1',
    landing_sections: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [areSectionsValid, setAreSectionsValid] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (isEdit) {
      setLoading(true);
      getProduct(id).then(p => {
        setForm({
          title: p.title || '',
          description: p.description || '',
          image_url: p.image_url || '',
          cta_buttons: p.cta_buttons || [],
          grid_size: p.grid_size || 'standard',
          is_published: p.is_published ?? true,
          order: p.order || 0,
          accent_color: p.accent_color || '#6366f1',
          landing_sections: p.landing_sections || [],
        });
        setLoading(false);
      }).catch(() => { toast.error("Produkt nicht gefunden"); navigate('/admin'); });
    }
  }, [id, isEdit, navigate, isAuthenticated]);

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return <PasswordGate onSuccess={() => setIsAuthenticated(true)} />;
  }

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Titel ist erforderlich");
      return;
    }
    if (!areSectionsValid) {
      toast.error("Bitte korrigiere zuerst das Landingpage-JSON");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateProduct(id, form);
        toast.success("Produkt aktualisiert");
      } else {
        await createProduct(form);
        toast.success("Produkt erstellt");
      }
      navigate('/admin');
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
    setSaving(false);
  };

  const addCTAButton = () => {
    setForm(prev => ({
      ...prev,
      cta_buttons: [...prev.cta_buttons, { text: '', url: '' }]
    }));
  };

  const updateCTAButton = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      cta_buttons: prev.cta_buttons.map((btn, i) =>
        i === index ? { ...btn, [field]: value } : btn
      )
    }));
  };

  const removeCTAButton = (index) => {
    setForm(prev => ({
      ...prev,
      cta_buttons: prev.cta_buttons.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-form-loading">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-product-form">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-slate-500 hover:text-slate-900 transition-colors" data-testid="product-form-back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-['Outfit'] font-bold text-lg text-slate-900">
              {isEdit ? 'Produkt bearbeiten' : 'Neues Produkt'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {isEdit && (
              <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={() => window.open(`/product/${id}`, '_blank')} data-testid="preview-product-btn">
                <Eye className="w-4 h-4" /> Vorschau
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} className="btn-gradient rounded-full gap-2" data-testid="save-product-btn">
              <Save className="w-4 h-4" /> {saving ? 'Speichert...' : 'Speichern'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
              <div>
                <Label htmlFor="title" className="text-slate-700 font-semibold">Titel</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-2 rounded-xl text-lg font-semibold"
                  placeholder="Produktname"
                  data-testid="product-title-input"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-slate-700 font-semibold">Beschreibung</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-2 rounded-xl"
                  placeholder="Beschreibe dein Produkt..."
                  rows={4}
                  data-testid="product-description-input"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-semibold">Produktbild</Label>
                <div className="mt-2">
                  <ImageUploader
                    value={form.image_url}
                    onChange={(path) => setForm(prev => ({ ...prev, image_url: path }))}
                  />
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-slate-700 font-semibold text-base">Buttons / Links</Label>
                <Button variant="outline" size="sm" onClick={addCTAButton} className="rounded-full gap-1" data-testid="add-cta-btn">
                  <Plus className="w-3 h-3" /> Button
                </Button>
              </div>
              {form.cta_buttons.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Keine Buttons vorhanden. Füge einen hinzu.</p>
              ) : (
                <div className="space-y-3">
                  {form.cta_buttons.map((btn, idx) => (
                    <div key={idx} className="flex gap-3 items-start" data-testid={`cta-row-${idx}`}>
                      <div className="flex-1 space-y-2">
                        <Input
                          value={btn.text}
                          onChange={(e) => updateCTAButton(idx, 'text', e.target.value)}
                          placeholder="Button-Text"
                          className="rounded-xl"
                          data-testid={`cta-text-${idx}`}
                        />
                        <Input
                          value={btn.url}
                          onChange={(e) => updateCTAButton(idx, 'url', e.target.value)}
                          placeholder="https://..."
                          className="rounded-xl"
                          data-testid={`cta-url-${idx}`}
                        />
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeCTAButton(idx)} className="text-slate-400 hover:text-red-500 mt-1" data-testid={`remove-cta-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <LandingSectionsEditor
              sections={form.landing_sections}
              onChange={(landingSections) => setForm((prev) => ({ ...prev, landing_sections: landingSections }))}
              onValidityChange={setAreSectionsValid}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
              <div>
                <Label className="text-slate-700 font-semibold">Status</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Switch
                    checked={form.is_published}
                    onCheckedChange={(v) => setForm(prev => ({ ...prev, is_published: v }))}
                    data-testid="product-published-switch"
                  />
                  <span className="text-sm text-slate-600">{form.is_published ? 'Veröffentlicht' : 'Entwurf'}</span>
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-semibold">Grid-Größe</Label>
                <Select
                  value={form.grid_size}
                  onValueChange={(v) => setForm(prev => ({ ...prev, grid_size: v }))}
                >
                  <SelectTrigger className="mt-2 rounded-xl" data-testid="product-grid-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (1x1)</SelectItem>
                    <SelectItem value="hero">Hero (2x2)</SelectItem>
                    <SelectItem value="wide">Breit (2x1)</SelectItem>
                    <SelectItem value="tall">Hoch (1x2)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700 font-semibold">Reihenfolge</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  className="mt-2 rounded-xl"
                  data-testid="product-order-input"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-semibold">Akzentfarbe (Landing Page)</Label>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="color"
                    value={form.accent_color}
                    onChange={(e) => setForm(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5"
                    data-testid="product-accent-color"
                  />
                  <Input
                    value={form.accent_color}
                    onChange={(e) => setForm(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="rounded-xl flex-1 font-mono text-sm"
                    placeholder="#e91e8c"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Farbe für Buttons, Highlights und Gradient-Blobs</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
